"use client";

import Link from "next/link";
import { Wordmark } from "./Logo";
import { BRAND } from "@/app/lib/content";
import { storeUrl } from "@/app/lib/appStores";
import { SocialLinks } from "./SocialLinks";

const COLUMNS: { title: string; links: { label: string; href: string }[] }[] = [
  {
    title: "Product",
    links: [
      { label: "Why Vowent", href: "/#why" },
      { label: "How it works", href: "/#encounters" },
      { label: "Safety", href: "/safety" },
      { label: "Child Safety", href: "/child-safety" },
      { label: "Download", href: "/download" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About us", href: "/about" },
      { label: "Support", href: "/support" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms & Conditions", href: "/terms" },
      { label: "Community Guidelines", href: "/community-guidelines" },
      { label: "Data Deletion", href: "/data-deletion" },
    ],
  },
];

export function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="site-footer">
      <div className="site-container py-16">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div className="max-w-xs">
            <Wordmark onDark />
            <p className="mt-4 text-[0.95rem] leading-relaxed text-white/70">
              {BRAND.tagline}
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5" aria-label="Vowent app availability">
              {(["ios", "android"] as const).map(platform => {
                const url = storeUrl(platform);
                const name = platform === "ios" ? "App Store" : "Google Play";
                const contents = <><span className="store-icon" aria-hidden="true">{platform === "ios" ? <svg viewBox="0 0 24 24"><path fill="currentColor" d="M16.7 12.8c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3-1.6-1.3-.1-2.5.8-3.1.8-.6 0-1.6-.8-2.7-.8-1.4 0-2.7.8-3.4 2.1-1.5 2.6-.4 6.3 1.1 8.4.7 1 1.6 2.1 2.8 2 1.1 0 1.6-.7 3-.7s1.8.7 3 .7c1.2 0 2-1 2.8-2 .8-1.1 1.1-2.2 1.1-2.3-.1 0-2.2-.8-2.2-3.4ZM14.7 6.7c.7-.8 1.2-1.9 1-3-.9 0-2 .6-2.7 1.4-.6.7-1.2 1.8-1 2.9 1 .1 2-.5 2.7-1.3Z" /></svg> : "▶"}</span><span><small>{url ? "Download on" : "Coming soon on"}</small>{name}</span></>;
                return url ? <a key={platform} className="store-badge" href={url} target="_blank" rel="noreferrer" aria-label={`Get Vowent on ${name}`}>{contents}</a> : <span key={platform} className="store-badge" aria-label={`${name} coming soon`}>{contents}</span>;
              })}
            </div>
            <SocialLinks />
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h3 className="eyebrow text-white/50">{col.title}</h3>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-[0.95rem] text-white/75 transition-colors hover:text-white"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-white/12 pt-7 text-[0.85rem] text-white/55 sm:flex-row sm:items-center sm:justify-between">
          <p>© {year} Owo360 Ltd. Vowent is a product of Owo360 Ltd.</p>
          <p className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span>You must be 21 or over to use Vowent.</span>
            <a
              href={`mailto:${BRAND.supportEmail}`}
              className="hover:text-white"
            >
              {BRAND.supportEmail}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
